import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, ChevronLeft, ChevronRight, Loader2, Search, X } from "lucide-react";
import { StatusBadge } from "../ui/DataPage";

// Building blocks for the Charging Station Management screens. Styling follows
// components/ui/DataPage.jsx (neutral scale, 13px tables, rounded-xl cards).

// ------------------------------------------------------------ buttons

const BTN = {
  primary: "bg-neutral-900 text-white hover:bg-neutral-800 disabled:bg-neutral-400",
  secondary: "border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50 disabled:text-neutral-400",
  ghost: "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 disabled:text-neutral-300",
  danger: "bg-red-600 text-white hover:bg-red-700 disabled:bg-red-300",
  link: "text-neutral-900 underline-offset-4 hover:underline disabled:text-neutral-400",
};

export const Button = ({ variant = "secondary", size = "md", loading = false, icon: Icon, children, className = "", ...rest }) => (
  <button
    type="button"
    className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/20 disabled:cursor-not-allowed ${
      variant === "link" ? "text-13" : size === "sm" ? "h-8 px-2.5 text-xs" : "h-9 px-3.5 text-13"
    } ${BTN[variant]} ${className}`}
    disabled={loading || rest.disabled}
    {...rest}
  >
    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : Icon ? <Icon className="h-4 w-4" /> : null}
    {children}
  </button>
);

export const IconButton = ({ icon: Icon, label, className = "", ...rest }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-40 ${className}`}
    {...rest}
  >
    <Icon className="h-4 w-4" />
  </button>
);

// ------------------------------------------------------------ status

export const STATUS_TONE = { active: "green", inactive: "neutral", upcoming: "amber" };

export const EntityStatus = ({ status }) => (
  <StatusBadge tone={STATUS_TONE[status] || "neutral"}>
    {status ? status.charAt(0).toUpperCase() + status.slice(1) : "—"}
  </StatusBadge>
);

export const YesNo = ({ value, yes = "Enabled", no = "Disabled" }) => (
  <span className={`text-13 font-medium ${value ? "text-neutral-900" : "text-neutral-400"}`}>{value ? yes : no}</span>
);

// ------------------------------------------------------------ overlays

// Escape closes only the top-most overlay (station drawer -> nested group modal)
const overlayStack = [];

function useOverlay(open, onClose) {
  const token = useRef({});
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const me = token.current;
    overlayStack.push(me);
    const onKey = (e) => {
      if (e.key === "Escape" && overlayStack[overlayStack.length - 1] === me) {
        e.stopPropagation();
        closeRef.current?.();
      }
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      const i = overlayStack.indexOf(me);
      if (i >= 0) overlayStack.splice(i, 1);
      if (overlayStack.length === 0) document.body.style.overflow = prevOverflow;
    };
  }, [open]);
}

// variant "modal": centred dialog; variant "drawer": full-height panel on the right
export const Overlay = ({ open, onClose, title, subtitle, children, footer, variant = "modal", width = "max-w-2xl", layer = "z-50" }) => {
  useOverlay(open, onClose);
  const titleId = useId();
  if (!open) return null;
  const isDrawer = variant === "drawer";
  return (
    <div className={`fixed inset-0 ${layer} flex ${isDrawer ? "justify-end" : "items-end justify-center p-0 sm:items-center sm:p-4"}`}>
      <div className="absolute inset-0 bg-neutral-950/40" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative flex w-full flex-col bg-white shadow-2xl ${width} ${
          isDrawer ? "h-full" : "max-h-[92vh] rounded-t-2xl sm:rounded-2xl"
        }`}
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-5 py-4 sm:px-6">
          <div className="min-w-0">
            <h2 id={titleId} className="text-base font-semibold text-neutral-900">{title}</h2>
            {subtitle && <p className="mt-0.5 text-13 text-neutral-500">{subtitle}</p>}
          </div>
          <IconButton icon={X} label="Close" onClick={onClose} className="-mr-1.5 -mt-1" />
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">{children}</div>
        {footer && <div className="flex flex-wrap items-center justify-end gap-2 border-t border-neutral-200 bg-neutral-50/60 px-5 py-3 sm:px-6">{footer}</div>}
      </div>
    </div>
  );
};

export const ConfirmDialog = ({ open, title, message, confirmLabel = "Confirm", tone = "danger", loading, onConfirm, onCancel }) => (
  <Overlay
    open={open}
    onClose={loading ? undefined : onCancel}
    title={title}
    width="max-w-md"
    layer="z-[70]"
    footer={
      <>
        <Button onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button variant={tone === "danger" ? "danger" : "primary"} loading={loading} onClick={onConfirm}>{confirmLabel}</Button>
      </>
    }
  >
    <p className="text-sm leading-6 text-neutral-600">{message}</p>
  </Overlay>
);

// ------------------------------------------------------------ form fields

export const inputClass = (error) =>
  `h-9 w-full rounded-lg border bg-white px-3 text-13 text-neutral-900 placeholder:text-neutral-400 transition-colors focus:outline-none focus:ring-2 disabled:bg-neutral-50 disabled:text-neutral-500 ${
    error ? "border-red-300 focus:border-red-400 focus:ring-red-500/15" : "border-neutral-200 focus:border-neutral-400 focus:ring-neutral-900/10"
  }`;

export const FormField = ({ label, required, error, hint, children, className = "", htmlFor }) => (
  <div className={`flex min-w-0 flex-col gap-1.5 ${className}`}>
    {label && (
      <label htmlFor={htmlFor} className="text-xs font-medium text-neutral-700">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
    )}
    {children}
    {error ? <p className="text-xs text-red-600">{error}</p> : hint ? <p className="text-xs text-neutral-500">{hint}</p> : null}
  </div>
);

// text/number/date/email input bound to form[name]
export const TextField = ({ label, name, form, errors, onChange, required, hint, className, ...rest }) => {
  const id = useId();
  return (
    <FormField label={label} required={required} error={errors?.[name]} hint={hint} className={className} htmlFor={id}>
      <input
        id={id}
        name={name}
        value={form[name] ?? ""}
        onChange={(e) => onChange(name, e.target.value)}
        className={inputClass(errors?.[name])}
        aria-invalid={Boolean(errors?.[name])}
        {...rest}
      />
    </FormField>
  );
};

export const SelectField = ({ label, name, form, errors, onChange, options, required, hint, className, ...rest }) => {
  const id = useId();
  return (
    <FormField label={label} required={required} error={errors?.[name]} hint={hint} className={className} htmlFor={id}>
      <select
        id={id}
        name={name}
        value={form[name] ?? ""}
        onChange={(e) => onChange(name, e.target.value)}
        className={`${inputClass(errors?.[name])} pr-8`}
        {...rest}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </FormField>
  );
};

export const Toggle = ({ checked, onChange, label, description, disabled }) => (
  <label className={`flex items-start justify-between gap-4 rounded-lg border border-neutral-200 px-3 py-2.5 ${disabled ? "opacity-60" : "cursor-pointer"}`}>
    <span className="min-w-0">
      <span className="block text-13 font-medium text-neutral-800">{label}</span>
      {description && <span className="mt-0.5 block text-xs text-neutral-500">{description}</span>}
    </span>
    <span className="relative mt-0.5 inline-flex flex-shrink-0">
      <input type="checkbox" className="peer sr-only" checked={Boolean(checked)} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="h-5 w-9 rounded-full bg-neutral-300 transition-colors peer-checked:bg-neutral-900 peer-focus-visible:ring-2 peer-focus-visible:ring-neutral-900/20" />
      <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
    </span>
  </label>
);

export const FormSection = ({ letter, title, description, children, action }) => (
  <section className="rounded-xl border border-neutral-200 bg-white">
    <div className="flex items-start justify-between gap-3 border-b border-neutral-100 px-4 py-3">
      <div className="flex min-w-0 items-start gap-3">
        {letter && (
          <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-neutral-900 text-[11px] font-semibold text-white">{letter}</span>
        )}
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
          {description && <p className="mt-0.5 text-xs text-neutral-500">{description}</p>}
        </div>
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

// ------------------------------------------------------------ searchable select

// options: [{ value, label, sub?, disabled? }]
export const SearchableSelect = ({ id, value, onChange, options, placeholder = "Select…", disabled, error, loading, emptyContent, footer }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listId = useId();

  const selected = options.find((o) => String(o.value) === String(value));
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => `${o.value} ${o.label} ${o.sub || ""}`.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    setTimeout(() => searchRef.current?.focus(), 0);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => setActive(0), [query, open]);

  const choose = (o) => {
    if (o.disabled) return;
    onChange(o.value);
    setOpen(false);
    setQuery("");
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[active]) choose(filtered[active]);
    } else if (e.key === "Escape") {
      e.stopPropagation();
      e.nativeEvent?.stopImmediatePropagation?.();
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative" onKeyDown={open ? onKeyDown : undefined}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        className={`${inputClass(error)} flex items-center justify-between gap-2 text-left`}
      >
        <span className={`truncate ${selected ? "text-neutral-900" : "text-neutral-400"}`}>
          {selected ? selected.label : loading ? "Loading…" : placeholder}
        </span>
        {loading ? <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-neutral-400" /> : <ChevronDown className="h-4 w-4 flex-shrink-0 text-neutral-400" />}
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-neutral-100 px-3">
            <Search className="h-4 w-4 text-neutral-400" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="h-9 w-full bg-transparent text-13 focus:outline-none"
            />
          </div>
          <ul id={listId} role="listbox" className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-6 text-center text-13 text-neutral-500">{query ? "No matches" : emptyContent || "Nothing to show"}</li>
            )}
            {filtered.map((o, i) => {
              const isSel = String(o.value) === String(value);
              return (
                <li
                  key={o.value}
                  role="option"
                  aria-selected={isSel}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(o)}
                  className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-13 ${i === active ? "bg-neutral-100" : ""} ${
                    o.disabled ? "cursor-not-allowed opacity-50" : ""
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-neutral-900">{o.label}</span>
                    {o.sub && <span className="block truncate text-xs text-neutral-500">{o.sub}</span>}
                  </span>
                  {isSel && <Check className="h-4 w-4 flex-shrink-0 text-neutral-900" />}
                </li>
              );
            })}
          </ul>
          {footer && <div className="border-t border-neutral-100 p-1">{footer(() => setOpen(false))}</div>}
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------ table helpers

export const SearchInput = ({ value, onChange, placeholder }) => (
  <div className="relative min-w-0 flex-1 sm:max-w-xs">
    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputClass()} pl-9`} />
  </div>
);

export const FilterSelect = ({ value, onChange, options, label }) => (
  <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputClass()} w-auto min-w-[140px] pr-8`}>
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

export const PAGE_SIZE = 10;

export function usePagination(items, pageSize = PAGE_SIZE, resetKey = "") {
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  useEffect(() => setPage(1), [resetKey]);
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);
  const slice = items.slice((page - 1) * pageSize, page * pageSize);
  return { page, pages, setPage, slice, total: items.length, from: items.length ? (page - 1) * pageSize + 1 : 0, to: Math.min(page * pageSize, items.length) };
}

export const Pagination = ({ page, pages, setPage, total, from, to }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 text-13 text-neutral-500">
    <span>
      {total === 0 ? "No results" : <>Showing <span className="font-medium text-neutral-800">{from}–{to}</span> of <span className="font-medium text-neutral-800">{total}</span></>}
    </span>
    <div className="flex items-center gap-1">
      <IconButton icon={ChevronLeft} label="Previous page" disabled={page <= 1} onClick={() => setPage(page - 1)} />
      <span className="min-w-[64px] text-center tabular-nums">{page} / {pages}</span>
      <IconButton icon={ChevronRight} label="Next page" disabled={page >= pages} onClick={() => setPage(page + 1)} />
    </div>
  </div>
);

// ------------------------------------------------------------ details

export const DetailGrid = ({ items }) => (
  <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
    {items.map(([label, value, wide]) => (
      <div key={label} className={wide ? "sm:col-span-2" : ""}>
        <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-neutral-500">{label}</dt>
        <dd className="mt-1 break-words text-13 text-neutral-900">{value === "" || value === null || value === undefined ? <span className="text-neutral-400">—</span> : value}</dd>
      </div>
    ))}
  </dl>
);

export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center px-6 py-12 text-center">
    {Icon && (
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-100 text-neutral-500">
        <Icon className="h-5 w-5" />
      </span>
    )}
    <p className="mt-3 text-sm font-medium text-neutral-900">{title}</p>
    {description && <p className="mt-1 max-w-sm text-13 text-neutral-500">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_RE = /^\+?[0-9\s-]{7,20}$/;
